import React, { memo } from "react";
import { AlertOctagon, BellRing, HeartPulse, ShieldOff } from "lucide-react";
import { Link } from "react-router-dom";
import { age, clockOf, words } from "./fmt";

/*
 * ALERTS PANEL — the server-side alert feed (audit_log rows the monitor
 * escalates) plus the three facts a buyer wants before trusting the
 * autotrader: is the kill switch on, is the monitor heartbeat fresh, and
 * would an alert actually reach a phone. Controlled; Dashboard polls
 * fetchAlertsFeed every 15 s.
 */

const SEVERITY = {
  critical: ["text-rose-300", "bg-rose-400"],
  error: ["text-rose-300", "bg-rose-400"],
  warning: ["text-amber-300", "bg-amber-400"],
  warn: ["text-amber-300", "bg-amber-400"],
  info: ["text-cyan-300", "bg-cyan-400"],
};

const sevClasses = (s) => SEVERITY[String(s || "").toLowerCase()] || ["text-slate-300", "bg-slate-400"];

/* One red line at the top of the page whenever the autotrader is halted or
 * the monitor has gone quiet — that must not hide behind a tab. */
export const SystemAlertStrip = memo(function SystemAlertStrip({ feed }) {
  if (!feed) return null;
  const halted = Boolean(feed.kill_switch);
  const stale = feed.monitor_ok === false;
  if (!halted && !stale) return null;
  return (
    <div data-testid="system-alert-strip" role="alert"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-rose-500/50 bg-rose-600 px-3 py-2 text-[11px] font-bold text-white">
      <AlertOctagon className="h-4 w-4 shrink-0" />
      {halted && <span>KILL SWITCH ON — no new entries until it is released</span>}
      {halted && stale && <span className="opacity-60">·</span>}
      {stale && <span>Monitor heartbeat stale ({age(feed.monitor_heartbeat_age_seconds)}) — stops may not be watched</span>}
      <Link to="/autotrader" className="ml-auto underline underline-offset-2">Open autotrader</Link>
    </div>
  );
});

function AlertsPanel({ feed }) {
  if (!feed) return <div className="p-4 text-center text-xs text-slate-500">Loading the alert feed…</div>;
  const halted = Boolean(feed.kill_switch);
  const ok = feed.monitor_ok !== false;
  const alerts = feed.alerts || [];
  return (
    <div className="p-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <div className={`rounded-lg border p-2 ${halted ? "border-rose-500/40 bg-rose-500/[.10]" : "border-emerald-500/25 bg-emerald-500/[.05]"}`}>
          <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500"><ShieldOff className="h-3 w-3" />Kill switch</p>
          <p className={`text-sm font-black ${halted ? "text-rose-300" : "text-emerald-300"}`}>{halted ? "ON — halted" : "off"}</p>
        </div>
        <div className={`rounded-lg border p-2 ${ok ? "border-emerald-500/25 bg-emerald-500/[.05]" : "border-rose-500/40 bg-rose-500/[.10]"}`}>
          <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500"><HeartPulse className="h-3 w-3" />Monitor heartbeat</p>
          <p className={`text-sm font-black tabular-nums ${ok ? "text-emerald-300" : "text-rose-300"}`}>
            {age(feed.monitor_heartbeat_age_seconds)} <span className="text-[10px] font-bold">{ok ? (feed.market_open ? "ok" : "ok · market closed") : "STALE"}</span>
          </p>
        </div>
        <div className={`rounded-lg border p-2 ${feed.alert_channel_configured ? "border-emerald-500/25 bg-emerald-500/[.05]" : "border-amber-500/35 bg-amber-500/[.08]"}`}>
          <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500"><BellRing className="h-3 w-3" />Alert channel</p>
          {feed.alert_channel_configured
            ? <p className="text-sm font-black text-emerald-300">configured</p>
            : <p className="text-[10px] font-bold leading-4 text-amber-300">not configured — set alert_webhook_url or Telegram under <Link to="/settings" className="underline underline-offset-2">Settings → alerts</Link></p>}
        </div>
      </div>
      <div className="mt-3 divide-y divide-slate-800/80 rounded-lg border border-line">
        {alerts.length ? alerts.map((a) => {
          const [text, dot] = sevClasses(a.severity);
          return (
            <div key={a.id ?? `${a.ts}-${a.message}`} className="flex items-start gap-2.5 px-3 py-2">
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <p className={`truncate text-[10px] font-black uppercase tracking-wider ${text}`}>
                    {words(a.event_type)}
                    {a.module ? <span className="ml-1 font-bold normal-case tracking-normal text-slate-600">· {a.module}</span> : null}
                  </p>
                  <span className="shrink-0 text-[10px] tabular-nums text-slate-600">{clockOf(a.ts)}</span>
                </div>
                <p className="mt-0.5 break-words text-[11px] leading-4 text-slate-300">{a.message}</p>
              </div>
            </div>
          );
        }) : <p className="px-3 py-4 text-center text-[11px] text-slate-600">No alerts in the last 48 hours.</p>}
      </div>
    </div>
  );
}

export default memo(AlertsPanel);
